import type { SystemStyleObject } from '@hover/blueprint';
import { cssVariables, forwardRef, transition } from '@hover/blueprint';
import type { As, ChakraProps, ComponentWithAs } from '@hover/blueprint/chakra';
import { resetFocus } from '@hover/blueprint/foundation';
import type { MotionStyle } from 'framer-motion';
import React from 'react';

export interface HideScrollbarsOptions {
  focus?: SystemStyleObject;
}

export const hideScrollbarsStyles = ({
  focus,
}: HideScrollbarsOptions = {}): SystemStyleObject => ({
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent' },
  outline: '2px solid transparent',
  outlineOffset: '-2px',
  ...resetFocus(
    focus ?? {
      outline: `2px solid ${cssVariables.color('darkAlpha.300').reference}`,
    },
  ),
  ...transition.easing250('outline'),
});

export const hideScrollbars = <Props extends ChakraProps, Element extends As>(
  Wrapped: ComponentWithAs<Element, Props>,
  options: HideScrollbarsOptions = {},
): ComponentWithAs<Element, Props> =>
  forwardRef<Props, any>((props, ref) => (
    <Wrapped
      borderRadius="200"
      ref={ref}
      {...props}
      sx={{ ...hideScrollbarsStyles(options), ...props.sx }}
    />
  ));

/**
 * Framer Motion doesn't automatically apply vendor prefixes, so this helper
 * prefixes any properties we find out still need prefixing for use with the
 * `style` prop on motion components.
 *
 * @param style style object to prefix (before passing to `style` on component)
 * @returns prefixed style object
 *
 * NOTE: We could use the `prefixer` plugin from the `stylis` module which is a
 * dependency of Emotion if we need more robust support, but I'd rather avoid
 * adding it as a direct dependency.
 *
 * @author jrolfs
 * @see {@link https://emotion.sh/docs/cache-provider}
 */
export const prefixMotionStyle = (style: MotionStyle): MotionStyle =>
  Object.entries(style).reduce((acc, [property, value]) => {
    switch (property) {
      case 'maskImage':
        return {
          ...acc,
          [property]: value,
          WebkitMaskImage: value,
        } as MotionStyle;
      default:
        return { ...acc, [property]: value };
    }
  }, {} as MotionStyle);

/**
 * Generate container queries for truncating multi-line text based on the height
 * of a container. For use with the `noOfLines` Chakra prop.
 *
 * @param height container height (in pixels) at which to start truncating text
 * @param lines number of lines to start truncation at when `height` is reached
 * @param step height (in pixels) that truncates another line
 *
 * @returns style object to be passed to `sx`
 */
export const responsiveLineClamp = (
  height: number,
  lines: number,
  step: number = 20,
): { [key: string]: { '--chakra-line-clamp': string } } => {
  let currentHeight = height;
  let currentLines = lines;

  const styles: Record<string, { '--chakra-line-clamp': string }> = {};

  while (currentHeight > 0 && currentLines > 0) {
    const query = `@container (height < ${currentHeight}px)`;
    const clamp = String(currentLines);

    styles[query] = { '--chakra-line-clamp': clamp };

    currentHeight -= step;
    currentLines -= 1;
  }

  return styles;
};

/**
 * Apply styles only when the user's device supports `:hover` states
 *
 * @param styles style object to apply
 * @returns style object wrapped in `hover` media query
 */
export const withHover = (styles: SystemStyleObject) => ({
  '@media (hover: hover)': styles,
});
